// Bundle Analysis and Performance Monitoring Utilities
import { 
  safeGetNavigationTiming, 
  safeGetPaintTiming, 
  safeGetResourceTiming, 
  safeGetLongTaskTiming,
  safeGetMemoryMetrics 
} from './performanceWrapper';

// Performance monitoring class
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.startTime = Date.now();
    this.observer = null;
    this.initializeObserver();
  }

    // Initialize Performance Observer for advanced metrics
  initializeObserver() {
    // In development mode, skip performance observer to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    
    if ('PerformanceObserver' in window) {
      this.observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          this.recordMetric(entry.name, entry);
        });
      });

      try {
        this.observer.observe({ entryTypes: ['navigation', 'resource', 'paint', 'largest-contentful-paint'] });
      } catch (e) {
        // Suppress warnings in development mode
        if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
          console.warn('Performance Observer not fully supported:', e);
        }
      }
    }
  }

  // Record performance metric
  recordMetric(name, value) {
    this.metrics.set(name, {
      ...value,
      timestamp: Date.now(),
      sessionTime: Date.now() - this.startTime
    });
  }

    // Get Core Web Vitals
  getCoreWebVitals() {
    // In development mode, return mock values to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return {
        fcp: 0,
        lcp: 0,
        fid: 0,
        cls: 0,
        tti: 0,
        tbt: 0
      };
    }
    
    try {
      const navigation = safeGetNavigationTiming();
      const paint = safeGetPaintTiming();

      return {
        // First Contentful Paint
        fcp: paint.fcp,
        
        // Largest Contentful Paint
        lcp: paint.lcp,
        
        // First Input Delay (approximation)
        fid: navigation.loadEventEnd - navigation.loadEventStart,
        
        // Cumulative Layout Shift (requires separate implementation)
        cls: this.calculateCLS(),
        
        // Time to Interactive (approximation)
        tti: navigation.domInteractive - navigation.navigationStart,
        
        // Total Blocking Time
        tbt: this.calculateTBT(navigation)
      };
    } catch (error) {
      // Suppress warnings in development mode
      if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
        console.warn('Core Web Vitals calculation failed:', error);
      }
      return {
        fcp: 0,
        lcp: 0,
        fid: 0,
        cls: 0,
        tti: 0,
        tbt: 0
      };
    }
  }

    // Calculate Cumulative Layout Shift
  calculateCLS() {
    // In development mode, return 0 to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return 0;
    }
    
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
    });

    try {
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // Suppress warnings in development mode
      if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
        console.warn('Layout shift observation not supported');
      }
    }

    return clsValue;
  }

    // Calculate Total Blocking Time
  calculateTBT(navigation) {
    // In development mode, return 0 to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return 0;
    }
    
    try {
      if (!navigation) return 0;
      
      const longTasks = safeGetLongTaskTiming();
      let tbt = 0;
      
      longTasks.forEach(task => {
        if (task.duration > 50) {
          tbt += task.duration - 50;
        }
      });
      
      return tbt;
    } catch (error) {
      // Suppress warnings in development mode
      if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
        console.warn('TBT calculation failed:', error);
      }
      return 0;
    }
  }

    // Get bundle loading performance
  getBundleMetrics() {
    // In development mode, return mock values to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return {
        totalResources: 0,
        jsResources: { count: 0, totalSize: 0, totalLoadTime: 0, largest: {} },
        fonts: 0,
        images: 0
      };
    }
    
    try {
      const resources = safeGetResourceTiming();
      const jsResources = resources.filter(r => r.name.includes('.js'));
      const cssResources = resources.filter(r => r.name.includes('.css'));

      return {
        totalResources: resources.length,
        jsResources: {
          count: jsResources.length,
          totalSize: jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0),
          totalLoadTime: jsResources.reduce((sum, r) => sum + r.duration, 0),
          largest: jsResources.reduce((largest, r) => 
            (r.transferSize || 0) > (largest.transferSize || 0) ? r : largest, {}
          )
        },
        cssResources: {
          count: cssResources.length,
          totalSize: cssResources.reduce((sum, r) => sum + (r.transferSize || 0), 0),
          totalLoadTime: cssResources.reduce((sum, r) => sum + r.duration, 0)
        },
        fonts: resources.filter(r => r.name.includes('font') || r.name.includes('.woff')).length,
        images: resources.filter(r => r.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)).length
      };
    } catch (error) {
      // Suppress warnings in development mode
      if (process.env.NODE_ENV === 'development' && !window.__warningsSuppressed) {
        console.warn('Bundle metrics calculation failed:', error);
      }
      return {
        totalResources: 0,
        jsResources: { count: 0, totalSize: 0, totalLoadTime: 0, largest: {} },
      };
    }
  }

  // Memory usage (if available)
  getMemoryMetrics() {
    return safeGetMemoryMetrics();
  }

  // Generate performance report
  generateReport() {
    const coreVitals = this.getCoreWebVitals();
    const bundleMetrics = this.getBundleMetrics();
    const memoryMetrics = this.getMemoryMetrics();

    return {
      timestamp: new Date().toISOString(),
      coreWebVitals: coreVitals,
      bundlePerformance: bundleMetrics,
      memoryUsage: memoryMetrics,
      recommendations: this.generateRecommendations(coreVitals, bundleMetrics),
      score: this.calculatePerformanceScore(coreVitals)
    };
  }

  // Generate optimization recommendations
  generateRecommendations(vitals, bundle) {
    const recommendations = [];

    if (vitals.fcp > 2000) {
      recommendations.push({
        type: 'critical',
        metric: 'First Contentful Paint',
        current: `${vitals.fcp}ms`,
        target: '<2000ms',
        suggestion: 'Reduce initial JavaScript bundle size and optimize critical rendering path'
      });
    }

    if (vitals.lcp > 2500) {
      recommendations.push({
        type: 'critical',
        metric: 'Largest Contentful Paint',
        current: `${vitals.lcp}ms`,
        target: '<2500ms',
        suggestion: 'Optimize images and lazy load non-critical resources'
      });
    }

    if (bundle.jsResources.totalSize > 500000) { // 500KB
      recommendations.push({
        type: 'warning',
        metric: 'JavaScript Bundle Size',
        current: `${(bundle.jsResources.totalSize / 1024).toFixed(1)}KB`,
        target: '<500KB',
        suggestion: 'Implement code splitting and remove unused dependencies'
      });
    }

    if (bundle.jsResources.count > 10) {
      recommendations.push({
        type: 'info',
        metric: 'Number of JS Files',
        current: bundle.jsResources.count,
        target: '<10',
        suggestion: 'Consider bundling smaller chunks together'
      });
    }

    return recommendations;
  }

  // Calculate overall performance score (0-100)
  calculatePerformanceScore(vitals) {
    let score = 100;

    // FCP scoring
    if (vitals.fcp > 3000) score -= 20;
    else if (vitals.fcp > 2000) score -= 10;

    // LCP scoring
    if (vitals.lcp > 4000) score -= 25;
    else if (vitals.lcp > 2500) score -= 15;

    // TTI scoring
    if (vitals.tti > 5000) score -= 20;
    else if (vitals.tti > 3000) score -= 10;

    // CLS scoring
    if (vitals.cls > 0.25) score -= 15;
    else if (vitals.cls > 0.1) score -= 8;

    return Math.max(0, Math.min(100, score));
  }

  // Start continuous monitoring
  startMonitoring(callback, interval = 30000) {
    return setInterval(() => {
      const report = this.generateReport();
      callback(report);
    }, interval);
  }

  // Cleanup
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Bundle size estimation utilities
export const BundleUtils = {
  // Estimate current bundle size from loaded resources
  estimateBundleSize() {
    // In development mode, return mock values to avoid deprecated API warnings
    if (process.env.NODE_ENV === 'development') {
      return {
        javascript: 0,
        css: 0,
        total: 0,
        formatted: {
          javascript: '0KB',
          css: '0KB',
          total: '0KB'
        }
      };
    }
    
    const resources = safeGetResourceTiming();
    const jsSize = resources
      .filter(r => r.name.includes('.js'))
      .reduce((sum, r) => sum + (r.transferSize || 0), 0);
    
    const cssSize = resources
      .filter(r => r.name.includes('.css'))
      .reduce((sum, r) => sum + (r.transferSize || 0), 0);

    return {
      javascript: jsSize,
      css: cssSize,
      total: jsSize + cssSize,
      formatted: {
        javascript: `${(jsSize / 1024).toFixed(1)}KB`,
        css: `${(cssSize / 1024).toFixed(1)}KB`,
        total: `${((jsSize + cssSize) / 1024).toFixed(1)}KB`
      }
    };
  },

  // Track lazy loading effectiveness
  trackLazyLoading() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Lazy loaded:', entry.target);
        }
      });
    });

    // Observe all images and iframes
    document.querySelectorAll('img[loading="lazy"], iframe[loading="lazy"]').forEach(el => {
      observer.observe(el);
    });

    return observer;
  },

  // Analyze unused CSS/JS (development helper)
  analyzeUnusedCode() {
    if (process.env.NODE_ENV === 'development') {
      console.log('Bundle analysis would run in production build');
      return;
    }

    // This would integrate with tools like PurgeCSS or webpack-bundle-analyzer
    const report = {
      timestamp: new Date().toISOString(),
      unusedCSS: 'Run PurgeCSS analysis for detailed unused CSS report',
      unusedJS: 'Run webpack-bundle-analyzer for detailed JavaScript analysis',
      recommendations: [
        'Use React.lazy for route-based code splitting',
        'Implement tree shaking for utility libraries',
        'Remove unused dependencies from package.json',
        'Use dynamic imports for large third-party libraries'
      ]
    };

    console.table(report);
    return report;
  }
};

// Initialize global performance monitor
export const performanceMonitor = new PerformanceMonitor();

// Development utilities
if (process.env.NODE_ENV === 'development') {
  // Add performance monitoring to window for debugging (but don't call deprecated APIs)
  window.performanceMonitor = performanceMonitor;
  window.bundleUtils = BundleUtils;
  
  // In development mode, just log that performance monitoring is available
  // but don't generate reports to avoid deprecated API warnings
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (!window.__consoleSuppressed && !window.__warningsSuppressed) {
        console.log('📊 Performance monitoring available (disabled in development to avoid warnings)');
        console.log('Use window.performanceMonitor.generateReport() manually if needed');
      }
    }, 1000);
  });
} 